import { useEffect } from "react"
import { router, useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/Button"
import { settings_task_settings_manual_trigger_path, settings_task_settings_path } from "@/lib/routes"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs"
import { NumberField } from "@/pages/Settings/components/PolicyFields"
import MonthDayPicker from "@/pages/Settings/components/MonthDayPicker"
import PeopleCircles from "@/components/PeopleCircles"
import ReviewerFields from "@/pages/Settings/components/ReviewerFields"
import SettingsSection from "@/pages/Settings/components/SettingsSection"
import type { Person } from "@/lib/person"
import type { MarketSetting } from "@/pages/Settings/TaskSettings"

// Filled once both dates and someone in charge are set. Days always hold a
// number and reviewers are optional, so neither counts.
function filled(m: MarketSetting) {
  return m.start_month != null && m.start_day != null && m.end_month != null && m.end_day != null
    && m.recipient_user_ids.length > 0
}

// One config row per market — markets run different planning calendars
// (VN Jan–Dec, US Jul–Jun), so dates, lead time, and PICs are all per market.
function MarketRow({ market, onChange, people, readOnly, disabled }: {
  market: MarketSetting
  onChange: (patch: Partial<MarketSetting>) => void
  people: Person[]
  readOnly: boolean
  disabled: boolean
}) {
  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex items-center gap-2">
        <span className="w-40 shrink-0 text-sm text-foreground">New plan start on</span>
        <MonthDayPicker
          month={market.start_month}
          day={market.start_day}
          onChange={({ month, day }) => onChange({ start_month: month, start_day: day })}
          disabled={disabled}
          label={`${market.name} plan start`}
        />
        <span className="text-xs text-muted-foreground">every year</span>
      </div>

      <div className="flex items-center gap-2">
        <span className="w-40 shrink-0 text-sm text-foreground">New plan end on</span>
        <MonthDayPicker
          month={market.end_month}
          day={market.end_day}
          onChange={({ month, day }) => onChange({ end_month: month, end_day: day })}
          disabled={disabled}
          label={`${market.name} plan end`}
        />
        <span className="text-xs text-muted-foreground">every year</span>
      </div>

      <p className="flex items-center gap-2 text-sm text-foreground">
        <span className="w-40 shrink-0">Task creation</span>
        <NumberField
          value={market.days_before}
          onChange={(n) => onChange({ days_before: n })}
          min={0}
          disabled={disabled}
          label={`${market.name} days before plan start`}
        />{" "}
        days before
      </p>

      <div className="flex items-start gap-2">
        <span className="w-40 shrink-0 pt-1.5 text-sm text-foreground">People in Charge</span>
        <PeopleCircles
          title="People in Charge"
          people={people}
          selectedIds={market.recipient_user_ids}
          onChange={(ids) => onChange({ recipient_user_ids: ids })}
          editable={!readOnly}
          disabled={disabled}
        />
      </div>

      <ReviewerFields
        skipReview={market.skip_review}
        reviewerIds={market.reviewer_user_ids}
        people={people}
        readOnly={readOnly}
        disabled={disabled}
        onChange={(patch) => onChange(patch)}
      />
    </div>
  )
}

// Its own form: Save sends only the `markets` rows (the controller touches
// only the arrays present in the request).
export default function TopDownPlanningCard({ id, markets, people, readOnly, onUnfilledChange }: {
  id: string
  markets: MarketSetting[]
  people: Person[]
  readOnly: boolean
  // Reports whether any market is still unfilled, from the live form values.
  onUnfilledChange?: (id: string, unfilled: boolean) => void
}) {
  const { data, setData, patch, processing } = useForm<{ markets: MarketSetting[] }>({ markets })
  const disabled = readOnly || processing

  const unfilled = data.markets.some((m) => !filled(m))
  useEffect(() => { onUnfilledChange?.(id, unfilled) }, [id, unfilled, onUnfilledChange])

  const updateMarket = (org_node_id: string, patchRow: Partial<MarketSetting>) => {
    setData("markets", data.markets.map((m) => (m.org_node_id === org_node_id ? { ...m, ...patchRow } : m)))
  }

  return (
    <SettingsSection
      id={id}
      title="Top Down Planning"
      action={!readOnly && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => router.post(settings_task_settings_manual_trigger_path(), {}, { preserveScroll: true })}
        >
          Manual Trigger
        </Button>
      )}
      canSave={!readOnly}
      processing={processing}
      onSave={() => patch(settings_task_settings_path(), { preserveScroll: true })}
    >
      {data.markets.length === 0 ? (
        <p className="text-sm text-muted-foreground">No markets yet — add country nodes in Org Structure first.</p>
      ) : (
        <Tabs defaultValue={data.markets[0].org_node_id} className="flex flex-col gap-5">
          <TabsList>
            {data.markets.map((market) => (
              <TabsTrigger key={market.org_node_id} value={market.org_node_id} dot={!filled(market)}>{market.name}</TabsTrigger>
            ))}
          </TabsList>
          {data.markets.map((market) => (
            <TabsContent key={market.org_node_id} value={market.org_node_id}>
              <MarketRow
                market={market}
                onChange={(patchRow) => updateMarket(market.org_node_id, patchRow)}
                people={people}
                readOnly={readOnly}
                disabled={disabled}
              />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </SettingsSection>
  )
}
